import React, { useState } from "react";
import { Image as ImageIcon, X, Maximize2 } from "lucide-react";

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const galleryItems = [
    { title: "Web3 DApp Interface", category: "UI/UX Design", desc: "Decentralized Finance & Staking Dashboard" },
    { title: "STQC Audit Dashboard", category: "Enterprise Portal", desc: "Security Compliance & Vulnerability Tracking" },
    { title: "Mobile Fintech Experience", category: "Mobile App", desc: "Cross-Platform Payment & Wallet Solutions" },
    { title: "Hickter AI Engine UI", category: "AI & ML", desc: "Interactive Streaming Chat Widget" },
    { title: "Data Digitization Workflow", category: "System Study", desc: "OCR & Legacy Database Restructuring" },
    { title: "Web3 NFT Marketplace", category: "Web3 Ecosystem", desc: "Multi-chain NFT Minting & Bidding Portal" }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-body py-16 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-12">
        
        {/* HEADER */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-cyan-500/10 px-3.5 py-1.5 rounded-full border border-blue-200 dark:border-cyan-500/20 font-heading">
            Visual Gallery & Designs
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white">
            Engineering & Design <span className="text-gradient-cyan">Showcase</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base font-medium">
            Take a visual tour of our website interfaces, mobile apps, and digital marketing design assets.
          </p>
        </div>

        {/* GALLERY GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryItems.map((item, idx) => (
            <div
              key={idx}
              className="glass-card rounded-2xl p-6 border border-white/10 space-y-4 group cursor-pointer hover:border-cyan-500/40 transition-all"
              onClick={() => setSelectedImage(item.title)}
            >
              <div className="w-full h-48 rounded-xl bg-gradient-to-br from-slate-900 via-slate-950 to-blue-950/60 border border-white/5 flex flex-col items-center justify-center space-y-2 relative overflow-hidden group-hover:scale-102 transition-transform">
                <ImageIcon size={36} className="text-cyan-400 group-hover:scale-110 transition-transform" />
                <span className="text-xs text-slate-400 font-medium font-heading">Preview Artifact</span>
                <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Maximize2 size={24} className="text-white" />
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-[11px] font-bold text-cyan-400 uppercase tracking-widest font-heading">{item.category}</span>
                <h3 className="font-heading text-lg font-bold text-white group-hover:text-cyan-300 transition-colors">{item.title}</h3>
                <p className="text-slate-400 text-xs">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* LIGHTBOX MODAL */}
        {selectedImage && (
          <div className="fixed inset-0 z-[500] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
            <div className="glass-panel p-8 rounded-3xl border border-white/20 max-w-xl w-full text-center space-y-6 relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute right-4 top-4 text-slate-400 hover:text-white p-1 rounded-lg bg-slate-900 border border-white/10"
              >
                <X size={20} />
              </button>

              <div className="w-full h-64 rounded-2xl bg-gradient-to-r from-blue-950 via-slate-900 to-violet-950 flex items-center justify-center border border-white/10">
                <ImageIcon size={48} className="text-cyan-400 animate-pulse" />
              </div>

              <div className="space-y-2">
                <h3 className="font-heading text-2xl font-bold text-white">{selectedImage}</h3>
                <p className="text-slate-400 text-xs">High-resolution interactive preview demo.</p>
              </div>

              <button onClick={() => setSelectedImage(null)} className="cyber-btn-primary text-xs">
                Close Preview
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
